import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyTax } from '../../interfaces/Tax/Tax';
import { createTax, deleteTax, getTax, updateTax } from '../../api/Tax';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';

export const ModifyTaxPage = () => {
  const navigate = useNavigate();

  const { taxID: taxIDAsStr } = useParams<string>();

  const taxID = taxIDAsStr ? Number(taxIDAsStr) : undefined;
  const isEditing = Number.isFinite(taxID);

  const [tax, setTax] = useState(getEmptyTax());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshTax = () => {
    setLoading(true);
    getTax(taxID!)
      .then((t) => setTax(t))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshTax();
    }
  }, [taxIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateTax(tax)
        .then((updatedTax) => {
          toast.success('Vergi başarıyla güncellendi');
          setTax(updatedTax);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createTax(tax)
        .then((createdTax) => {
          toast.success('Vergi başarıyla oluşturuldu');
          navigate(`/products/taxes/${createdTax.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteTax(tax.id)
      .then(() => {
        toast.success('Vergi başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/products/taxes');
  const invalidTax = tax.multiplier < 0;

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili vergiyi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Vergiyi Düzenle' : 'Yeni Vergi'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={tax.title}
          onChange={(e) => setTax({ ...tax, title: e.target.value })}
          fullWidth
        />
        <NumberInput
          label='Oran'
          value={tax.multiplier}
          onChange={(newMultiplier) =>
            setTax({ ...tax, multiplier: newMultiplier })
          }
          acceptFloat
          error={invalidTax}
          helperText={invalidTax ? 'Minimum değer: 0' : undefined}
          endAdornment={<Typography>%</Typography>}
        />
        <Button
          variant='contained'
          disabled={invalidTax}
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Paper>
    </Box>
  );
};
